import { useCallback, useEffect, useRef } from "react";
import { useNotifications } from "../context/NotificationContext";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import type { Notification } from "../types/notifications";
import { PUSHER_RECONNECTED_EVENT } from "../utils/pusherConnection";
import {
  PAGE_LOADED_AT,
  RELEASE_NOTE_EVENT,
  getToastedIds,
  isToastableReleaseNote,
  markToasted,
  releaseNoteToastKey,
} from "../utils/releaseNotes";
import { hideReleaseNoteToast, notifyReleaseNote } from "../utils/notify";

/** Minimum gap between catch-up fetches (mirrors ClientDocumentsTab's throttle). */
export const RELEASE_NOTE_CATCH_UP_THROTTLE_MS = 30_000;

/** How many unread release notes one catch-up fetch asks for. */
const CATCH_UP_PAGE_SIZE = 20;

export interface UseReleaseNoteWatcherOptions {
  /** When the page loaded; injectable for tests. Defaults to PAGE_LOADED_AT. */
  pageLoadedAt?: number;
  /** Catch-up throttle window; injectable for tests. */
  throttleMs?: number;
}

/**
 * Raises the "new version available → Refresh" toast for release notes
 * published after this tab loaded.
 *
 * Notes arrive two ways: live over Pusher (usePusher → addNotification), and —
 * because a backgrounded mobile tab's websocket is suspended and misses events
 * — via a throttled catch-up fetch of unread release notes whenever the tab
 * becomes visible, is restored from the bfcache, comes back online, or Pusher
 * reconnects. Both paths land in NotificationContext; this hook derives the
 * toasts from `allNotifications`, toasts each note once per tab session, and
 * closes a toast once its note is read (e.g. from the bell or another tab's
 * Refresh picked up by the list).
 */
export function useReleaseNoteWatcher({
  pageLoadedAt = PAGE_LOADED_AT,
  throttleMs = RELEASE_NOTE_CATCH_UP_THROTTLE_MS,
}: UseReleaseNoteWatcherOptions = {}): void {
  const { allNotifications, upsertNotifications, markNotificationAsRead } =
    useNotifications();
  const { getNotifications } = useApp();
  const { isAuthenticated } = useAuth();

  // Latest callbacks in refs so window/document listeners are bound once and
  // never churn when context values are re-created.
  const getNotificationsRef = useRef(getNotifications);
  getNotificationsRef.current = getNotifications;
  const upsertRef = useRef(upsertNotifications);
  upsertRef.current = upsertNotifications;
  const markReadRef = useRef(markNotificationAsRead);
  markReadRef.current = markNotificationAsRead;
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  // Ids toasted in this tab (seeded from sessionStorage so a soft re-mount
  // never re-toasts), and the ones whose toast is currently on screen.
  const toastedRef = useRef<Set<string> | null>(null);
  if (toastedRef.current === null) {
    toastedRef.current = new Set(getToastedIds());
  }
  const shownRef = useRef<Set<string>>(new Set());
  const lastCheckRef = useRef(0);
  const inFlightRef = useRef(false);

  // Toast new notes; close toasts whose note has since been read.
  useEffect(() => {
    if (!isAuthenticated) return;
    const toasted = toastedRef.current as Set<string>;
    (allNotifications || []).forEach((notification: Notification) => {
      if (notification?.event_name !== RELEASE_NOTE_EVENT) return;
      const key = releaseNoteToastKey(notification);
      if (notification.is_read) {
        if (shownRef.current.has(key)) {
          shownRef.current.delete(key);
          hideReleaseNoteToast(key);
        }
        return;
      }
      if (!isToastableReleaseNote(notification, toasted, pageLoadedAt)) return;
      toasted.add(key);
      markToasted(key);
      shownRef.current.add(key);
      const notificationId = notification.notification_id;
      notifyReleaseNote({
        releaseNoteId: key,
        title: notification.body?.title || "A new version is available",
        summary: notification.body?.message || "",
        link: notification.body?.link,
        onRefresh: () => markReadRef.current(notificationId),
        onClose: () => {
          shownRef.current.delete(key);
        },
      });
    });
  }, [allNotifications, isAuthenticated, pageLoadedAt]);

  // On logout, close any toast still on screen; the next user starts fresh.
  useEffect(() => {
    if (isAuthenticated) return;
    shownRef.current.forEach((key) => hideReleaseNoteToast(key));
    shownRef.current.clear();
  }, [isAuthenticated]);

  const catchUp = useCallback(async () => {
    const now = Date.now();
    if (inFlightRef.current) return;
    if (now - lastCheckRef.current < throttleMs) return;
    lastCheckRef.current = now;
    inFlightRef.current = true;
    try {
      const response = await getNotificationsRef.current(
        1,
        CATCH_UP_PAGE_SIZE,
        { event_name: RELEASE_NOTE_EVENT, is_read: false },
      );
      const list = response?.notifications;
      // Signed out while the request was in flight: drop the stale result.
      if (!isAuthenticatedRef.current) return;
      if (Array.isArray(list) && list.length > 0) {
        upsertRef.current(list);
      }
    } catch (error) {
      console.warn("Release-note catch-up fetch failed:", error);
    } finally {
      inFlightRef.current = false;
    }
  }, [throttleMs]);

  // Mobile catch-up triggers. Bound only while signed in.
  useEffect(() => {
    if (!isAuthenticated) return;
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      void catchUp();
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      void catchUp();
    };
    const onOnline = () => {
      void catchUp();
    };
    const onReconnected = () => {
      void catchUp();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("online", onOnline);
    window.addEventListener(PUSHER_RECONNECTED_EVENT, onReconnected);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("online", onOnline);
      window.removeEventListener(PUSHER_RECONNECTED_EVENT, onReconnected);
    };
  }, [isAuthenticated, catchUp]);
}

export default useReleaseNoteWatcher;
